/**
 * /partner/listings/:assetType  → index of partner's drafts + published
 *                                  listings for the given asset type.
 *
 * Pulls from /api/partner-listings/{assetType} (server filters by country
 * and partner). Renders each listing with cover image, status pill, and a
 * progress pill showing completion-%. CTA: open in wizard.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ArrowLeft, Plus, Pencil, Trash2, Loader2, Home, Car, Ship, Palette, Image as ImageIcon, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import Nav from '@/components/Nav';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { getAuthHeaders } from '@/utils/auth';
import { useAuth } from '@/context/AuthContext';
import { useCurrency } from '@/context/CurrencyContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ASSET_META = {
  property:        { label: 'Properties',           Icon: Home,    titleField: 'title' },
  car:             { label: 'Luxury Cars',          Icon: Car,     titleField: 'name' },
  yacht:           { label: 'Yachts',               Icon: Ship,    titleField: 'name' },
  interior_design: { label: 'Interior Projects',    Icon: Palette, titleField: 'title' },
};

const StatusPill = ({ status }) => (
  <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.15em] font-semibold ring-1 ${
    status === 'published'
      ? 'bg-emerald-500/10 text-emerald-300 ring-emerald-500/30'
      : 'bg-amber-500/10 text-amber-300 ring-amber-500/30'
  }`} data-testid="status-pill">
    {status || 'draft'}
  </span>
);

const CompletionPill = ({ pct }) => {
  if (typeof pct !== 'number') return null;
  return (
    <span
      className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.15em] font-semibold ring-1 ${
        pct >= 100 ? 'bg-gold/15 text-gold ring-gold/40' : 'bg-white/5 text-text-secondary ring-white/15'
      }`}
      data-testid="completion-pill"
    >
      {pct}% complete
    </span>
  );
};

const buildTitle = (assetType, item) => {
  if (assetType === 'car' || assetType === 'yacht') {
    return item.name || [item.brand, item.model].filter(Boolean).join(' ') || item.title || 'Untitled';
  }
  return item.title || item.project_name || item.name || 'Untitled';
};

const buildSubtitle = (assetType, item) => {
  if (assetType === 'property') {
    return [item.area, item.locality, item.city].filter(Boolean).join(', ') || item.location || '';
  }
  if (assetType === 'car') return [item.year, item.variant, item.condition].filter(Boolean).join(' · ');
  if (assetType === 'yacht') return [item.year, item.length_ft && `${item.length_ft} ft`, item.condition].filter(Boolean).join(' · ');
  return [item.category, item.style, item.location].filter(Boolean).join(' · ');
};

const PartnerListingsIndexPage = () => {
  const { assetType } = useParams();
  const navigate = useNavigate();
  const { formatPrice } = useCurrency();
  const { user } = useAuth();

  const meta = ASSET_META[assetType];

  const [loading, setLoading] = useState(true);
  const [listings, setListings] = useState([]);
  const [completion, setCompletion] = useState({});
  const [filter, setFilter] = useState('all'); // all | draft | published
  const [query, setQuery] = useState('');
  const [country, setCountry] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Load listings + completion-% in one shot
  const fetchListings = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/partner-listings/${assetType}`, { headers: getAuthHeaders() });
      const rows = data.listings || [];
      setListings(rows);
      setCountry(data.country_id);

      // Validate every row to surface completion %.
      if (rows.length === 0) {
        setCompletion({});
      } else {
        const entries = await Promise.all(rows.map(async (l) => {
          try {
            const { data: rep } = await axios.post(
              `${API}/partner-listings/${assetType}/${l.id}/validate`,
              null,
              { headers: getAuthHeaders() },
            );
            return [l.id, rep];
          } catch { return [l.id, null]; }
        }));
        const map = {};
        entries.forEach(([id, rep]) => { if (rep) map[id] = rep; });
        setCompletion(map);
      }
    } catch (e) {
      toast.error('Failed to load listings');
      setListings([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!meta) return;
    fetchListings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetType]);

  const filtered = useMemo(() => {
    return listings.filter((l) => {
      if (filter !== 'all') {
        const status = l.status || (l.published ? 'published' : 'draft');
        if (status !== filter) return false;
      }
      if (query.trim()) {
        const hay = `${buildTitle(assetType, l)} ${buildSubtitle(assetType, l)}`.toLowerCase();
        if (!hay.includes(query.trim().toLowerCase())) return false;
      }
      return true;
    });
  }, [listings, filter, query, assetType]);

  const counts = useMemo(() => {
    const c = { all: listings.length, draft: 0, published: 0 };
    listings.forEach((l) => {
      const status = l.status || (l.published ? 'published' : 'draft');
      if (status === 'draft') c.draft += 1;
      else if (status === 'published') c.published += 1;
    });
    return c;
  }, [listings]);

  if (!meta) {
    return (
      <div className="min-h-screen bg-obsidian text-text-primary">
        <Nav variant="partner" showBack partnerLabel="Partner Portal" companyName={user?.company_name} />
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 py-12 text-center">
          <h1 className="font-playfair text-2xl mb-2">Unknown asset type: {assetType}</h1>
          <Button onClick={() => navigate('/partner/listings/new')} className="bg-gold text-black hover:bg-gold-light">Choose an asset type</Button>
        </div>
      </div>
    );
  }

  const apiOrigin = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
  const fullUrl = (url) => (!url ? '' : url.startsWith('http') ? url : `${apiOrigin}${url}`);
  const coverOf = (l) => l.cover_image
    || (Array.isArray(l.gallery_images) && l.gallery_images[0])
    || (Array.isArray(l.images) && l.images[0])
    || null;

  const onDelete = async (id) => {
    try {
      await axios.delete(`${API}/partner-listings/${assetType}/${id}`, { headers: getAuthHeaders() });
      toast.success('Listing deleted');
      setDeleteConfirm(null);
      fetchListings();
    } catch {
      toast.error('Failed to delete listing');
    }
  };

  const Icon = meta.Icon;

  return (
    <div className="min-h-screen bg-obsidian text-text-primary mobile-pb" data-testid="partner-listings-index">
      <Nav variant="partner" showBack partnerLabel="Partner Portal" companyName={user?.company_name} />

      <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-10">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 text-text-secondary hover:text-gold text-sm mb-6"
          data-testid="listings-index-back"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-xl bg-gold/10 ring-1 ring-gold/20 flex items-center justify-center">
                <Icon className="w-5 h-5 text-gold" />
              </div>
              <h1 className="font-playfair text-3xl md:text-4xl font-semibold">My {meta.label}</h1>
              {country && (
                <span className="text-[10px] uppercase tracking-[0.18em] text-text-secondary bg-white/5 ring-1 ring-white/10 px-2 py-0.5 rounded-full">
                  {country}
                </span>
              )}
            </div>
            <p className="text-text-secondary text-sm">All drafts and published {meta.label.toLowerCase()} bound to your partner country.</p>
          </div>
          <Button
            onClick={() => navigate(`/partner/listings/new/${assetType}`)}
            className="bg-gold text-black hover:bg-gold-light"
            data-testid="add-new-listing-btn"
          >
            <Plus className="w-4 h-4 mr-2" /> New listing
          </Button>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2 mb-6">
          {['all', 'draft', 'published'].map((k) => (
            <button
              key={k}
              onClick={() => setFilter(k)}
              data-testid={`filter-${k}`}
              className={`px-3 py-1.5 rounded-full text-xs uppercase tracking-[0.16em] ring-1 transition-colors ${
                filter === k
                  ? 'bg-gold/15 text-gold ring-gold/40'
                  : 'bg-white/[0.03] text-text-secondary ring-white/10 hover:ring-white/30'
              }`}
            >
              {k} <span className="opacity-60 ml-1">({counts[k]})</span>
            </button>
          ))}
          <div className="ml-auto w-full sm:w-72">
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search listings…"
              className="bg-white/5 border-white/10"
              data-testid="search-input"
            />
          </div>
        </div>

        {loading ? (
          <div className="glass-card p-12 text-center text-text-secondary flex items-center justify-center gap-2">
            <Loader2 className="w-5 h-5 animate-spin" /> Loading listings…
          </div>
        ) : filtered.length === 0 ? (
          <div className="glass-card p-12 text-center" data-testid="empty-state">
            <Icon className="w-14 h-14 text-gold/60 mx-auto mb-3" />
            <h3 className="font-playfair text-2xl font-semibold mb-1">
              {listings.length === 0 ? `No ${meta.label.toLowerCase()} yet` : 'No matches'}
            </h3>
            <p className="text-text-secondary text-sm mb-4">
              {listings.length === 0
                ? `Add your first listing to start receiving leads.`
                : 'Try a different filter or search keyword.'}
            </p>
            {listings.length === 0 && (
              <Button onClick={() => navigate(`/partner/listings/new/${assetType}`)} className="bg-gold text-black hover:bg-gold-light" data-testid="empty-add-btn">
                <Plus className="w-4 h-4 mr-2" /> Create your first listing
              </Button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {filtered.map((l, idx) => {
              const cover = coverOf(l);
              const title = buildTitle(assetType, l);
              const subtitle = buildSubtitle(assetType, l);
              const price = l.price ?? l.price_from ?? null;
              const status = l.status || (l.published ? 'published' : 'draft');
              const pct = completion[l.id]?.total_pct;
              const livePath = assetType === 'property' ? `/properties/${l.id}` : `/marketplace/${l.id}`;
              const isLive = status === 'published' || l.published === true;
              return (
                <motion.div
                  key={l.id}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: Math.min(idx * 0.04, 0.3) }}
                  className="glass-card overflow-hidden flex flex-col"
                  data-testid={`listing-card-${l.id}`}
                >
                  <div className="aspect-[16/10] bg-white/5 overflow-hidden">
                    {cover ? (
                      <img src={fullUrl(cover)} alt={title} className="w-full h-full object-cover" loading="lazy" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-text-secondary">
                        <ImageIcon className="w-8 h-8 opacity-30" />
                      </div>
                    )}
                  </div>
                  <div className="p-5 flex flex-col flex-1">
                    <h4 className="font-semibold text-base mb-1 line-clamp-1" title={title}>{title}</h4>
                    {subtitle && <p className="text-text-secondary text-xs mb-3 line-clamp-1">{subtitle}</p>}
                    <div className="flex items-center gap-2 flex-wrap mb-3">
                      <StatusPill status={status} />
                      <CompletionPill pct={pct} />
                      {isLive && (
                        <a
                          href={livePath}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.15em] font-semibold ring-1 bg-emerald-500/10 text-emerald-300 ring-emerald-500/30 hover:bg-emerald-500/15 transition-colors"
                          title="Preview how customers see this listing (opens user portal in a new tab)"
                          data-testid={`view-live-pill-${l.id}`}
                        >
                          <ExternalLink className="w-3 h-3" /> Live
                        </a>
                      )}
                    </div>
                    {price != null && (
                      <div className="text-gold font-semibold mb-3">{formatPrice(price)}</div>
                    )}
                    <div className="mt-auto pt-3 border-t border-white/5 flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex-1 border-white/20 text-white hover:bg-white/10 hover:text-gold"
                        onClick={() => navigate(`/partner/listings/${assetType}/${l.id}`)}
                        data-testid={`edit-listing-${l.id}`}
                      >
                        <Pencil className="w-3.5 h-3.5 mr-1.5" /> Open
                      </Button>
                      {deleteConfirm === l.id ? (
                        <div className="flex gap-1 flex-1">
                          <Button size="sm" onClick={() => onDelete(l.id)} className="flex-1 bg-red-600 text-white hover:bg-red-700" data-testid={`confirm-delete-${l.id}`}>Yes</Button>
                          <Button size="sm" variant="outline" onClick={() => setDeleteConfirm(null)} className="flex-1 border-white/20 text-white hover:bg-white/10">No</Button>
                        </div>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setDeleteConfirm(l.id)}
                          className="flex-1 border-red-500/30 text-red-400 hover:bg-red-500/10 hover:text-red-300"
                          data-testid={`delete-listing-${l.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Delete
                        </Button>
                      )}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default PartnerListingsIndexPage;
